import { Grid, Modal as ModalModule, UI, Utils } from 'common';
import { AllCarriers } from 'common/src/code/data/carriers';
import neutralizeCsvFormula from 'common/src/code/utils/neutralizeCsvFormula';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import React, { Component } from 'react';

import AccountCommunication from '../utils/AccountCommunication';
import AddCarrierModal from './AddCarrierModal.component';
import AutoFundModal from './AutoFundModal.component';
import LocationSettingsModal from './LocationSettingsModal.component';
import LocationsResultModal from './LocationsResultModal.component';
import PermissionsModal from './PermissionsModal.component';
import RegionsModal from './RegionsModal.component';
import RequestNewLocationsModal from './RequestNewLocationsModal.component';

const { Section, SectionBody, WrappedToolbar, Spinner, WrappedButtonType } = UI;
const {
	Logger,
	Session,
	UserRole,
	UserCaps,
	Util,
	withLayoutAndModalContexts,
} = Utils;
const { GridPanel } = Grid;
const { ModalButtonActionType } = ModalModule;

// this is the max money value that DB allows (truncating the decimal portion)
const MAXIMUM_LIMIT_AMOUNT = 922337203685477;

class Locations extends Component {
	constructor() {
		super();

		this.state = {
			columnDefs: this.buildColumnDefs(),
			regions: [],
			locations: [],
			rowData: [],
			rowDataDisplayed: [],
			searchString: '',
			selectedLocations: [],
			isLoading: false,
			loadingMessage: '',
			hasFetchedFullLocationData: false,
		};

		this.gridApi = null;

		// the corp info of the user (corp or region admin)
		this.userCorpInfo = {};
	}

	/**
	 * Return columnDefs array based on user's role.
	 * @returns {array}
	 */
	buildColumnDefs = () => {
		const userRole = Session.getUserRole();

		// all UC value columns
		const userCapsColumns = [
			{
				headerName: 'Add Funds',
				field: 'AddFunds',
				width: 100,
			},
			{
				headerName: 'Reports',
				field: 'ViewReports',
				width: 120,
			},
			{
				headerName: 'Store Enabled',
				field: 'BuySupplies',
				width: 120,
			},
			{
				headerName: 'Cost Codes Required',
				field: 'CostCodesRequired',
				width: 150,
			},
			{
				headerName: 'Auto-Fund',
				field: 'AutoFundEnabled',
				width: 150,
			},
		].map(column => ({
			...column,
			valueFormatter: ({ value }) => this.processUserCapsColumnValue(value),
		}));

		// build columns
		return [
			{
				headerName: 'Name',
				field: 'locationName',
				cellRenderer: params => {
					const link = document.createElement('a');
					link.href = '#';
					link.innerText = params.value;
					link.addEventListener('click', e => {
						e.preventDefault();
						this.editLocations(params.data.location);
					});
					return link;
				},
				filter: true,
				headerCheckboxSelection: true,
				checkboxSelection: true,
				sortable: true,
				sort: 'asc',
				flex: 1,
				minWidth: 200, // this make sure the text doesn't block selection checkbox
			},
			// WEBAPPS-25229, display location status (active or closed)
			{
				headerName: 'Status',
				field: 'locationStatus',
				filter: true,
				sortable: true,
				width: 90,
			},
			// per WEBAPPS-24304,
			// Region column is rendered only for corp admin users
			userRole === UserRole.CorpAdmin
				? {
						headerName: 'Region',
						field: 'region',
						filter: true,
						sortable: true,
					}
				: null,
			{
				headerName: 'Users',
				field: 'usersCount',
				filter: true,
				sortable: true,
				width: 80,
			},
			{
				headerName: 'Per Print Limit',
				field: 'perPrintLimit',
				filter: true,
				width: 120,
				sortable: true,
				valueFormatter: ({ value }) => this.processPerPrintLimitValue(value),
			},
			...userCapsColumns,
			{
				headerName: 'Carriers',
				field: 'carriers',
				width: 300,
				sortable: true,
				hide: true,
				comparator: (valueA, valueB) => {
					if (valueA === valueB) {
						return 0;
					}
					// Sorts by the number of carriers in a row. Based on the number of commas
					return valueA.split(',').length > valueB.split(',').length ? 1 : -1;
				},
			},
		].filter(column => column);
	};

	getUserCorpInfo = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { userId } = Session.getAccountInfo();

		const userCorpInfoResponse =
			await AccountCommunication.getUserCorpInfo(userId);

		// process userCorpInfo response
		if (AccountCommunication.isSuccessful(userCorpInfoResponse)) {
			// store information
			this.userCorpInfo = isObject(userCorpInfoResponse.data)
				? userCorpInfoResponse.data
				: {};

			// call get regions followed by get locations
			this.getRegions();
		} else {
			showError({ response: userCorpInfoResponse });
			this.setState({
				isLoading: false,
				loadingMessage: '',
			});
		}
	};

	/**
	 * Make API call to retrieve list of available regions. This call is made first, and
	 * calls getLocations once the state is updated.
	 */
	getRegions = async () => {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await AccountCommunication.getRegions();

		if (AccountCommunication.isSuccessful(response)) {
			// set the list of regions to the state, then call getLocations so regions are available for locations functionality
			this.setState(
				{
					regions: response.data,
				},
				() => this.getLocations(response.data)
			);
		} else {
			showError({ response });
			this.setState({
				isLoading: false,
				loadingMessage: '',
			});
		}
	};

	/**
	 * Make API call to retrieve list of locations.
	 * @param {array} regionsData Regions data from GetRegions endpoint.
	 */
	getLocations = async (regionsData, type) => {
		const {
			modalContext: { showError },
		} = this.props;
		const { regions, searchString } = this.state;

		let response;

		if (type === 'full') {
			response = await AccountCommunication.getFullLocations();
		} else {
			response = await AccountCommunication.getLocations();
		}

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});

		if (!AccountCommunication.isSuccessful(response)) {
			showError({
				response,
			});

			return;
		}

		// build locations data for the grid
		const rowData = this.processLocationsData(
			response.data,
			regionsData || regions
		);

		this.setState({
			rowData,
			locations: response.data,
			rowDataDisplayed: rowData,
			hasFetchedFullLocationData: type === 'full',
		});

		// filter the grid by any existing search criteria
		this.filterBySearch(searchString);
	};

	appendAccountInfoToLocations = async locations => {
		const response =
			await AccountCommunication.appendAccountInfoToLocations(locations);
		const {
			modalContext: { showError },
		} = this.props;

		if (!AccountCommunication.isSuccessful(response)) {
			showError(response);
		} else {
			return response;
		}
	};

	processLocationsData = (data = [], regionsData = []) => {
		const regions = isArray(regionsData) ? regionsData : [];

		const buildCarriersColumn = location => {
			if (typeof location.ConfiguredCarriers === 'string') {
				return 'Loading...';
			} else {
				return location.ConfiguredCarriers?.length > 0
					? `USPS, ${location.ConfiguredCarriers?.map(
							c => find(AllCarriers, ['value', c.carrier]).displayName
						).join(', ')}`
					: 'USPS';
			}
		};

		return isArray(data)
			? data.map(location => {
					// MeterAccount of the location
					const meterAccount = isObject(location.MeterAccount)
						? location.MeterAccount
						: null;

					// check if per-print limit exceeds maximum value allowed
					const exceedPerPrintLimitMaximum =
						isObject(meterAccount) &&
						isNumber(meterAccount.PerPrintLimit) &&
						meterAccount.PerPrintLimit > MAXIMUM_LIMIT_AMOUNT;

					return {
						location:
							exceedPerPrintLimitMaximum === true
								? {
										...location,
										MeterAccount: {
											...meterAccount,

											// per WEBAPPS-24338, per-print limit cannot exceed maximum
											PerPrintLimit: MAXIMUM_LIMIT_AMOUNT,
										},
									}
								: location,
						custId: location.CustId,

						// Users column
						usersCount: location.ActiveUsersCount,

						// Name column
						locationName: meterAccount
							? `${meterAccount.Name} (${meterAccount.MeterNo})`
							: '',

						// Location Status column
						locationStatus: location.IsClosed ? 'Closed' : 'Active',

						// Region column
						region: meterAccount
							? regions.reduce(
									(_name, region) =>
										_name === '' && region.Id === meterAccount.CorpId
											? region.Name
											: _name,
									''
								)
							: '',

						// Per Print Limit column
						perPrintLimit: meterAccount ? meterAccount.PerPrintLimit : 0,

						// following are for UC Value columns

						// Add Funds column
						AddFunds: Util.hasBitSet(
							meterAccount.Capabilities,
							UserCaps.ucPurchasePostage
						),

						// Reports column
						ViewReports: Util.hasBitSet(
							meterAccount.Capabilities,
							UserCaps.ucWebBasedReports || UserCaps.ucAdvancedReporting
						),

						// Store Enabled column
						BuySupplies: Util.hasBitSet(
							meterAccount.Capabilities,
							UserCaps.ucPurchaseFromStore
						),

						// Cost Codes Required
						CostCodesRequired: !Util.hasBitSet(
							meterAccount.Capabilities,
							UserCaps.ucReferenceCodeOptional
						),

						AutoFundEnabled: location.AutoPurchase?.Enabled,

						// Carrier column
						carriers: buildCarriersColumn(location),
					};
				})
			: [];
	};

	/**
	 * Format Per-Print Limit value as US currency.
	 * @param {number} value
	 * @return {string}
	 */
	processPerPrintLimitValue = value => {
		// Use JavaScript Intl.NumberFormat to format per-print limit value
		const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});

		if (value >= MAXIMUM_LIMIT_AMOUNT) {
			return 'No Limit';
		} else if (!isNumber(value)) {
			return 'N/A';
		}

		return usCurrencyFormatter.format(value);
	};

	/**
	 * Format Add Funds,Reports, Store Enabled, Cost Codes Required value as Yes/No.
	 * @param {boolean} value
	 * @return {string}
	 */
	processUserCapsColumnValue = value => {
		if (value === undefined) {
			return '';
		}

		return value ? 'Yes' : 'No';
	};

	showCarrierModalAsync = async activeLocations => {
		const { data: updatedActiveLocations } =
			await this.appendAccountInfoToLocations(activeLocations);
		const {
			modalContext: { showModal },
		} = this.props;

		if (updatedActiveLocations.length > 1) {
			showModal({
				component: AddCarrierModal,
				data: {
					selectedLocations: updatedActiveLocations,
					callback: async result => {
						if (result) {
							// on bulk edit save, refresh locations
							this.onUpdateSuccess();
						}
						Logger.logToServer({
							category: 'bulk_location_dialog',
							action: 'close_dialog',
							number_locations: activeLocations.length,
						});
					},
				},
			});
		} else {
			this.editLocations(updatedActiveLocations[0]);
		}
	};

	/**
	 * Functionality to open the appropriate modal (single location settings or bulk edit) when either a location name or the
	 * edit button is clicked. If a location is passed in, that will be used. Otherwise, selected location will be pulled from
	 * the state.
	 */
	editLocations = async (location, category) => {
		const { regions, selectedLocations } = this.state;
		const {
			modalContext: { closeModal, showModal, showMessage },
		} = this.props;
		const { customerDetails = {} } = Session.getAccountInfo();
		const parentPerPrintLimit = customerDetails.perPrintLimit;

		if (selectedLocations.length === 1 || location) {
			const { data: updatedLocation } = await this.appendAccountInfoToLocations(
				[location || selectedLocations[0]]
			);

			// open single location settings
			const locationSettingsModalId = showModal({
				component: LocationSettingsModal,
				data: {
					parentPerPrintLimit,
					singleLocation: updatedLocation[0],
					regions,
					MAXIMUM_LIMIT_AMOUNT,
					userCorpInfo: this.userCorpInfo,
					callback: response => {
						if (
							AccountCommunication.isSuccessful(response) ||
							response === true
						) {
							// refresh locations
							this.onUpdateSuccess();

							// close the modal
							closeModal(locationSettingsModalId);
						}
					},
				},
			});

			Logger.logToServer({
				category: 'location_dialog',
				action: 'view_dialog',
			});
		} else if (category === 'permissions') {
			// open bulk edit with selectedLocations
			showModal({
				component: PermissionsModal,
				data: {
					parentPerPrintLimit,
					regions,
					selectedLocations,
					MAXIMUM_LIMIT_AMOUNT,
					callback: response => {
						if (AccountCommunication.isSuccessful(response)) {
							// on bulk edit save, refresh locations
							this.onUpdateSuccess();
						}

						showModal({
							component: LocationsResultModal,
							data: {
								response,
								editCategory: 'Permission',
								locations: selectedLocations.length,
							},
						});

						Logger.logToServer({
							category: 'bulk_location_dialog',
							action: 'close_dialog',
							saved: AccountCommunication.isSuccessful(response),
							number_locations: selectedLocations.length,
						});
					},
				},
			});
		} else if (category === 'region') {
			showModal({
				component: RegionsModal,
				data: {
					regions,
					selectedLocations,
					callback: response => {
						if (AccountCommunication.isSuccessful(response)) {
							// on bulk edit save, refresh locations
							this.onUpdateSuccess();
						}

						showModal({
							component: LocationsResultModal,
							data: {
								response,
								editCategory: 'Region',
								locations: selectedLocations.length,
							},
						});

						Logger.logToServer({
							category: 'bulk_location_dialog',
							action: 'close_dialog',
							saved: AccountCommunication.isSuccessful(response),
							number_locations: selectedLocations.length,
						});
					},
				},
			});
		} else if (category === 'autofund') {
			const { AutoFundThresholdLimit } = Session.getSettings();
			showModal({
				component: AutoFundModal,
				data: {
					selectedLocations,
					AutoFundThresholdLimit,
					callback: response => {
						if (AccountCommunication.isSuccessful(response)) {
							// on bulk edit save, refresh locations
							this.onUpdateSuccess();
						}

						showModal({
							component: LocationsResultModal,
							data: {
								response,
								editCategory: 'Auto Fund',
								locations: selectedLocations.length,
							},
						});

						Logger.logToServer({
							category: 'bulk_location_dialog',
							action: 'close_dialog',
							saved: AccountCommunication.isSuccessful(response),
							number_locations: selectedLocations.length,
						});
					},
				},
			});
		} else if (category === 'addCarrier') {
			const activeLocations = selectedLocations.filter(
				l => l.AccountStatus === 'Active'
			);
			const { hasFetchedFullLocationData } = this.state;

			const showCarrierModal = () => {
				if (activeLocations.length > 1) {
					showModal({
						component: AddCarrierModal,
						data: {
							selectedLocations: activeLocations,
							callback: async result => {
								if (result) {
									// on bulk edit save, refresh locations
									this.onUpdateSuccess();
								}
								Logger.logToServer({
									category: 'bulk_location_dialog',
									action: 'close_dialog',
									number_locations: activeLocations.length,
								});
							},
						},
					});
				} else if (!hasFetchedFullLocationData) {
					this.showCarrierModalAsync(activeLocations);
				} else {
					this.editLocations(activeLocations[0]);
				}
			};

			if (activeLocations.length === 0) {
				showMessage({
					data: {
						title: 'Unable to Add Carrier',
						description:
							'Carriers can only be added to active locations. Please select at least one active location and try again.',
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'OK',
					},
				});
			} else if (activeLocations.length < selectedLocations.length) {
				showMessage({
					data: {
						title: 'Warning',
						description: `${selectedLocations.length - activeLocations.length} of ${
							selectedLocations.length
						} selected locations are inactive or closed. Carriers can only be added to active locations. Remove those selections to proceed?`,
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: `Proceed with ${activeLocations.length} Locations`,
					},
					closeCallback: () => {
						if (!hasFetchedFullLocationData) {
							this.showCarrierModalAsync(activeLocations);
						} else {
							showCarrierModal();
						}
					},
				});
			} else if (!hasFetchedFullLocationData) {
				this.showCarrierModalAsync(activeLocations);
			} else {
				showCarrierModal();
			}
		}
	};

	/**
	 * Event handler for edit location button
	 */
	onEditLocationsClick = category => {
		this.editLocations(null, category);
	};

	onRequestNewLocationsClick = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: RequestNewLocationsModal,
		});
	};

	/**
	 * Set gridApi property to AG Grid's api when the grid loads.
	 */

	onUpdateSuccess = () => {
		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Updating locations...',
				selectedLocations: [],
			},
			() => {
				this.getLocations();
				this.gridApi.deselectAll();
			}
		);
	};

	onGridReady = ({ api }) => {
		this.gridApi = api;
		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Loading locations...',
			},
			() => {
				const userRole = Session.getUserRole();

				// get user's corp info object (this is done the same in Users component)
				if (userRole === UserRole.CorpAdmin) {
					this.getUserCorpInfo();
				} else if (userRole === UserRole.RegionAdmin) {
					// per WEBAPPS-24304, skip regions call for region admin users
					// region admins only get locations information.
					this.getLocations();
				}
			}
		);
	};

	/**
	 * Set ag-grid's sort model to default.
	 */
	onSortChanged = sortModel => {
		this.gridApi.setSortModel(sortModel);
	};

	/**
	 * Called when grid selection is changed. Determines which and how many rows are selected and sets the state data
	 * accordingly.
	 */
	onSelectionChanged = () => {
		const selectedRows = this.gridApi.getSelectedRows();
		const selectedLocations = selectedRows.map(location => location.location);
		this.setState({ selectedLocations });
	};

	onColumnVisible = async column => {
		const { colId, visible } = column;
		const { locations, regions } = this.state;
		let rowData;

		if (colId === 'carriers' && visible) {
			locations.forEach(l => (l.ConfiguredCarriers = 'loading...'));
			rowData = this.processLocationsData(locations, regions);

			this.setState({
				rowDataDisplayed: rowData,
			});

			this.getLocations(null, 'full');
		}
	};

	/**
	 * Updates the grid filter based on search criteria.
	 */
	filterBySearch = searchStr => {
		this.setState(({ rowData }) => ({
			rowDataDisplayed: rowData.filter(
				({ locationName = '' }) =>
					locationName.toLowerCase().indexOf(searchStr.toLowerCase()) !== -1
			),
			searchString: searchStr,
		}));
	};

	/**
	 * Exports the grid to a CSV file with header row. Note that all records are exported, not just selected rows.
	 */
	exportToCsv = () => {
		const { rowData } = this.state;
		const { customerDetails = {} } = Session.getAccountInfo();
		const username = customerDetails.username || '';

		Logger.saveActionData('Export Locations Button Clicked');
		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'export_button',
		});

		this.gridApi.exportDataAsCsv({
			fileName: `${username}Locations.csv`,
			processCellCallback: ({ column: { colId }, value }) => {
				if (
					colId === 'AddFunds' ||
					colId === 'ViewReports' ||
					colId === 'BuySupplies' ||
					colId === 'CostCodesRequired'
				) {
					return this.processUserCapsColumnValue(value);
				} else if (colId === 'perPrintLimit') {
					return `$${value}`;
				} else {
					return neutralizeCsvFormula(value);
				}
			},
		});

		Logger.logToServer({
			category: 'export_locations',
			location_count: rowData.length,
		});
	};

	render() {
		const {
			columnDefs,
			rowDataDisplayed,
			selectedLocations,
			isLoading,
			loadingMessage,
		} = this.state;
		const numberOfSelectedRows = selectedLocations.length;
		const {
			layoutContext: { isMobile },
		} = this.props;
		const locationText = numberOfSelectedRows === 1 ? 'Location' : 'Locations';
		const editLocationLabel = `Edit ${numberOfSelectedRows || ''} ${locationText}`;
		const editLocationTooltipText = `${'<b>Edit</b>'}\nEdit selected ${locationText.toLowerCase()}.`;
		const userRole = Session.getUserRole();
		const isCorpAdmin = userRole === UserRole.CorpAdmin;
		const editLocationsModel = [
			{
				command: () => this.onEditLocationsClick('permissions'),
				label: 'Permissions',
			},
			{
				command: () => this.onEditLocationsClick('autofund'),
				label: 'Auto Funding',
			},
			{
				command: () => this.onEditLocationsClick('addCarrier'),
				label: 'Add Carrier',
			},
		];

		// only render Region option for Corp Admin users
		if (isCorpAdmin) {
			editLocationsModel.splice(1, 0, {
				command: () => this.onEditLocationsClick('region'),
				label: 'Region',
			});
		}

		// buttons used for WrappedToolbar
		const buttons = [
			{
				label: 'Request New Locations',
				icon: 'add-icon',
				className: 'icon-btn',
				hide: !isCorpAdmin,
				group: 'left',
				onClick: this.onRequestNewLocationsClick,
				tooltip: `${'<b>Request New Locations</b>'}\nRequest new locations for your account.`,
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				label: editLocationLabel,
				icon: 'edit-icon',
				className: 'icon-btn',
				disabled: numberOfSelectedRows === 0,
				hide: numberOfSelectedRows > 1,
				group: 'left',
				onClick: this.onEditLocationsClick,
				tooltip: editLocationTooltipText,
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				label: `Edit ${numberOfSelectedRows || ''} ${locationText}`,
				disabled: false,
				icon: 'edit-icon',
				className: 'split-edit-btn icon-btn',
				group: 'left',
				hide: numberOfSelectedRows < 2,
				model: editLocationsModel,
				buttonType: WrappedButtonType.TertiaryGrey,
				tooltip: editLocationTooltipText,
				tooltipOptions: {
					position: 'bottom',
				},
			},
			{
				icon: 'export-icon',
				className: `export-btn${isMobile ? ' hidden' : ''}`,
				label: 'Export',
				group: 'right',
				hidden: true,
				onClick: this.exportToCsv,
				tooltip: `${'<b>Export</b>'}\nExport Locations to a csv file.`,
				tooltipOptions: {
					position: 'bottom',
				},
			},
		];

		return (
			<div className="account-locations">
				<Section>
					<WrappedToolbar
						buttons={buttons}
						dataqa="locations-toolbar"
						searchCallback={this.filterBySearch}
						searchInputPlaceholder="Search Locations"
						showSearch
					/>
					<SectionBody noPadding>
						<GridPanel
							defaultColDef={{ suppressSizeToFit: true }}
							rowData={rowDataDisplayed}
							columnDefs={columnDefs}
							showPagination={false}
							onGridReady={this.onGridReady}
							onSortChanged={this.onSortChanged}
							onSelectionChanged={this.onSelectionChanged}
							onColumnVisible={this.onColumnVisible}
							className="locations-grid"
							dataqa="locations-grid"
						/>
					</SectionBody>
				</Section>
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(Locations);
